"use client"

import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { cn, buildUrl } from '@/shared/lib/utils'
import HomeOwnerForm from '@/features/homeowners/components/forms/home-owner-form'
import UpdateHomeOwnerFormProvider from '@/features/homeowners/components/forms/providers/update-home-owner-form-provider'
import HomeDetailsFormAccordion, { ALL_ACCORDION_ITEMS, DEFAULT_OPEN_ITEMS } from '@/features/homeowners/components/home-details-form-accordion'
import UpdateHomeFormProvider from '@/features/homeowners/components/forms/providers/update-home-form-provider'
import { ProjectNotesTaggedProvider } from '@/features/homeowners/context/project-notes-tagged-context'
import { OrganizationOptionsProvider } from '@/features/homeowners/context/organization-options-context'
import { HomeOwner } from '@/features/homeowners/types/backend-response.type'
import { Home } from '@/features/homeowners/types/api-response/home-api-response.type'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import {
  shouldMaskHomeownerData,
  maskHomeownerPhone,
  maskHomeownerAddress,
} from '@/shared/lib/utils/homeowner-privacy.utils'
import PhotoTimelineTab from '@/features/homeowners/components/photo-timeline-tab'
import WorkflowTab from '@/features/homeowners/components/workflow-tab'
import { storeReturnUrl } from '@/shared/lib/utils/navigation-helpers'
import Link from 'next/link'
import { Button } from '@/shared/components/ui/button'
import { updateHomeReportedStatus } from '@/features/homeowners/api/homes.api'
import successToast from '@/shared/lib/toast/success.toast'
import errorToast from '@/shared/lib/toast/error.toast'
import { useQueryClient } from '@tanstack/react-query'
import { isNoEmailPlaceholderValue } from '@/features/volunteers/schemas/volunteers.schema'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'
import { buildGoogleMapsPlaceUrl, isValidLatLngPair } from '@/shared/lib/utils/google-maps-url'
import { getDisplayAddressValue, isUnknownLocationValue } from '@/features/homeowners/lib/unknown-location'

type HomeownerDetailsTabsProps = {
  homeOwner: HomeOwner
  home: Home | null
}

// Helper function to format American phone numbers
const formatPhoneNumber = (phone: string | null | undefined): string => {
  if (!phone) return 'N/A'

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  // Check if it's an American number (10 digits or 11 digits starting with 1)
  if (digits.length === 11 && digits.startsWith('1')) {
    // Remove the leading 1 and format
    const cleaned = digits.substring(1)
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
  } else if (digits.length === 10) {
    // Format 10-digit number
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`
  }

  // Return original if not a standard American format
  return phone
}

// Helper function to get phone digits for tel: link
const getPhoneDigits = (phone: string | null | undefined): string | null => {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  // Return with +1 prefix for US numbers
  if (digits.length === 10) {
    return `+1${digits}`
  } else if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`
  }
  return phone.startsWith('+') ? phone : `+${digits}`
}

type TabValue = 'homeowner' | 'home' | 'photos' | 'workflow'

const HOMEOWNER_DETAILS_TABS = [
  {
    label: 'Home',
    value: 'home' as const,
    requiresRole: undefined, // Available to all users
  },
  {
    label: 'Homeowner',
    value: 'homeowner' as const,
    requiresRole: [UserRole.ADMIN, UserRole.BASE_LEADER],
  },
  {
    label: 'Photos',
    value: 'photos' as const,
    requiresRole: undefined, // Available to all users
  },
  {
    label: 'Workflow',
    value: 'workflow' as const,
    requiresRole: [UserRole.ADMIN], // Admin only
  },
] as const

export default function HomeownerDetailsTabs({ homeOwner, home }: HomeownerDetailsTabsProps) {
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()
  const { session } = useSession()
  const queryClient = useQueryClient()
  const canViewHomeownerDetails = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  const canViewWorkflow = session?.role === UserRole.ADMIN
  const shouldMaskPhone = shouldMaskHomeownerData(session?.role, session?.sub, homeOwner.user?.id)
  // The homeowner response establishes whether a home exists before the full
  // home-details query completes. Use it so the tab bar does not shift later.
  const hasHome = Boolean(homeOwner.home?.id)

  // Active/Inactive toggle state (synced from home.isReported)
  const [reportedStatus, setReportedStatus] = useState(home?.isReported ?? true)
  useEffect(() => {
    setReportedStatus(home?.isReported ?? true)
  }, [home?.isReported])
  const [reportedStatusLoading, setReportedStatusLoading] = useState(false)
  const handleReportedStatusChange = useCallback(
    async (homeId: number, newStatus: boolean) => {
      setReportedStatusLoading(true)
      setReportedStatus(newStatus)
      try {
        const result = await updateHomeReportedStatus(homeId, newStatus)
        if (result.success) {
          successToast(`Home reported status updated to ${newStatus ? 'On' : 'Off'}`)
          await queryClient.invalidateQueries({ queryKey: ['homes', 'filter-search'] })
        } else {
          errorToast(result.message ?? 'Failed to update status')
          setReportedStatus(!newStatus)
        }
      } catch {
        errorToast('Failed to update status')
        setReportedStatus(!newStatus)
      } finally {
        setReportedStatusLoading(false)
      }
    },
    [queryClient]
  )

  // Store return URL on first load (only if coming from a different page)
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const fromParam = searchParams.get('from')
    if (fromParam) {
      // searchParams.get() returns already-decoded values. decodeURIComponent would throw URIError
      // when the value contains literal % (e.g. workscopeStatus=25%,50%,75%) because % is not followed by hex.
      const returnUrl = fromParam
      // Only store if we're coming from a different base path (not just tab change)
      const currentBasePath = pathname.split('?')[0]
      try {
        const returnBasePath = new URL(returnUrl, window.location.origin).pathname.split('?')[0]
        
        // If the return path is different from current path, store it
        if (returnBasePath !== currentBasePath) {
          storeReturnUrl(returnUrl, homeOwner.id.toString())
        }
      } catch (e) {
        // If returnUrl is not a full URL, treat it as a path
        const returnBasePath = returnUrl.startsWith('/') ? returnUrl.split('?')[0] : `/${returnUrl.split('?')[0]}`
        if (returnBasePath !== currentBasePath) {
          storeReturnUrl(returnUrl, homeOwner.id.toString())
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount - we intentionally don't want to re-run when dependencies change

  // Get initial tab from URL or default to 'home' if available, otherwise 'homeowner' if visible
  const getInitialTab = (): TabValue => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'home' || tabParam === 'homeowner' || tabParam === 'photos' || tabParam === 'workflow') {
      // If tab is 'homeowner', verify user has permission
      if (tabParam === 'homeowner') {
        if (canViewHomeownerDetails) {
          return 'homeowner'
        }
        // Fallback to home if homeowner tab not available
        return 'home'
      }
      // If tab is 'photos', verify home exists
      if (tabParam === 'photos') {
        if (hasHome) {
          return 'photos'
        }
        // Fallback to home if photos tab not available
        return canViewHomeownerDetails ? 'homeowner' : 'home'
      }
      // If tab is 'workflow', verify user has permission and home exists
      if (tabParam === 'workflow') {
        if (canViewWorkflow && hasHome) {
          return 'workflow'
        }
        // Fallback to home if workflow tab not available
        return canViewHomeownerDetails ? 'homeowner' : 'home'
      }
      // Select Home immediately. Its content renders a loading state until
      // the full home-details query resolves.
      if (tabParam === 'home') {
        return 'home'
      }
      return tabParam as TabValue
    }
    
    // Default: prefer Home whenever the homeowner has a home.
    if (hasHome) {
      return 'home'
    }
    return canViewHomeownerDetails ? 'homeowner' : 'home'
  }

  const [activeTab, setActiveTab] = useState<TabValue>(getInitialTab())
  const [openAccordionItems, setOpenAccordionItems] = useState<string[]>([])

  // Update active tab when search params or home availability change
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'home' || tabParam === 'homeowner' || tabParam === 'photos' || tabParam === 'workflow') {
      if (tabParam === 'homeowner') {
        if (canViewHomeownerDetails) {
          setActiveTab('homeowner')
        } else {
          setActiveTab('home')
        }
      } else if (tabParam === 'photos') {
        if (hasHome) {
          setActiveTab('photos')
        } else {
          setActiveTab(canViewHomeownerDetails ? 'homeowner' : 'home')
        }
      } else if (tabParam === 'workflow') {
        if (canViewWorkflow && hasHome) {
          setActiveTab('workflow')
        } else {
          setActiveTab(canViewHomeownerDetails ? 'homeowner' : 'home')
        }
      } else {
        setActiveTab('home')
      }
    } else if (hasHome) {
      // No tab in URL: prefer Home whenever a home is known to exist.
      setActiveTab('home')
    }
  }, [searchParams, canViewHomeownerDetails, canViewWorkflow, hasHome])

  // Function to handle tab change and update URL
  const handleTabChange = (tabValue: TabValue) => {
    setActiveTab(tabValue)
    // Update URL with tab parameter, preserving other search params
    const currentParams: Record<string, string | string[]> = {}
    searchParams.forEach((value, key) => {
      if (key !== 'tab') { // Preserve all params except tab
        const existing = currentParams[key]
        if (existing) {
          currentParams[key] = Array.isArray(existing) 
            ? [...existing, value]
            : [existing as string, value]
        } else {
          currentParams[key] = value
        }
      }
    })
    const url = buildUrl(pathname, { tab: tabValue, ...currentParams })
    // Use replace instead of push so tab changes don't create history entries
    // This allows the browser back button to go directly to the original referring page
    router.replace(url)
  }

  // Filter tabs based on user role and availability
  const visibleTabs = HOMEOWNER_DETAILS_TABS.filter(tab => {
    if (tab.value === 'homeowner') {
      // Hide homeowner tab if user doesn't have permission
      return canViewHomeownerDetails
    }
    if (tab.value === 'photos') {
      return hasHome
    }
    if (tab.value === 'workflow') {
      return canViewWorkflow && hasHome
    }
    // Home is visible as soon as the homeowner response identifies a home.
    if (tab.value === 'home') {
      return hasHome
    }
    return true
  })

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-5">
        {visibleTabs.map((tab) => {
          const isActive = activeTab === tab.value
          
          return (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={cn(
                'w-[100px] text-center pb-0.5 border-b-[3px] border-transparent',
                {
                  "border-primary text-primary font-medium": isActive,
                }
              )}
            >
              {tab.label}
            </button>
          )
        })}
      </div>
      
      <div className="mt-6">
        {activeTab === 'homeowner' && (
          <UpdateHomeOwnerFormProvider homeOwner={homeOwner}>
            <HomeOwnerForm
              homeId={home?.id ?? null}
              isUpdateMode={true}
              canEditEmailOnUpdate={isNoEmailPlaceholderValue(homeOwner.user?.email)}
            />
          </UpdateHomeOwnerFormProvider>
        )}
        {activeTab === 'home' && (
          <>
            {home ? (
              <>
                {/* Address and Phone Section with Expand/Collapse All Button */}
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-5">
                  <div className="rounded-lg border border-input p-4 flex-1">
                    <div className="flex flex-col md:flex-row md:justify-between gap-4">
                      {/* Address */}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-muted-foreground mb-1">Address</p>
                        {(() => {
                          const displayAddress = getDisplayAddressValue(home?.address || homeOwner.home?.address)
                          const lat = home?.latitude ?? homeOwner.home?.latitude ?? null
                          const lng = home?.longitude ?? homeOwner.home?.longitude ?? null
                          const hasCoords = isValidLatLngPair(lat, lng)
                          const mapsHref = buildGoogleMapsPlaceUrl(home?.address || homeOwner.home?.address, lat, lng)
                          const hasMapTarget = mapsHref !== '#'
                          const isUnknownLocation = isUnknownLocationValue(home?.address || homeOwner.home?.address)

                          if (!displayAddress?.trim() && !hasMapTarget) {
                            return <p className="text-base text-muted-foreground">N/A</p>
                          }

                          if (shouldMaskPhone) {
                            return (
                              <p className="text-base text-foreground break-words">
                                {displayAddress?.trim()
                                  ? isUnknownLocation
                                    ? displayAddress
                                    : maskHomeownerAddress(displayAddress)
                                  : hasCoords
                                    ? 'Location on file'
                                    : 'N/A'}
                              </p>
                            )
                          }

                          if (!hasMapTarget) {
                            return (
                              <p className="text-base text-foreground break-words">
                                {displayAddress?.trim() || 'N/A'}
                              </p>
                            )
                          }

                          return (
                            <Link
                              href={mapsHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-base text-foreground break-words hover:text-primary hover:underline transition-colors"
                            >
                              {displayAddress?.trim() || 'View on Google Maps'}
                            </Link>
                          )
                        })()}
                      </div>
                      {/* Phone */}
                      <div className="min-w-0">
                        <p className="text-sm text-muted-foreground mb-1">Phone</p>
                        {homeOwner.user.phone ? (
                          shouldMaskPhone ? (
                            <p className="text-base text-foreground whitespace-nowrap">
                              {maskHomeownerPhone(homeOwner.user.phone)}
                            </p>
                          ) : (
                            <>
                              {/* Mobile: Show as link */}
                              <Link
                                href={`tel:${getPhoneDigits(homeOwner.user.phone)}`}
                                className="text-base text-foreground whitespace-nowrap hover:text-primary hover:underline transition-colors md:hidden"
                              >
                                {formatPhoneNumber(homeOwner.user.phone)}
                              </Link>
                              {/* Desktop: Show as text only */}
                              <p className="text-base text-foreground whitespace-nowrap hidden md:block">
                                {formatPhoneNumber(homeOwner.user.phone)}
                              </p>
                            </>
                          )
                        ) : (
                          <p className="text-base text-muted-foreground">N/A</p>
                        )}
                      </div>
                      {/* Date Entered */}
                      <div className="min-w-0 md:text-right">
                        <p className="text-sm text-muted-foreground mb-1">Date Entered</p>
                        <p className="text-base text-foreground whitespace-nowrap">
                          {home.createdAt
                            ? new Date(home.createdAt).toLocaleDateString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  {/* Expand All + Active/Inactive: same row on mobile/small, stacked on md+ */}
                  <div className="flex flex-row flex-wrap items-center gap-2 md:flex-col md:items-end">
                    {(() => {
                      const allOpen = openAccordionItems.length === ALL_ACCORDION_ITEMS.length
                      const buttonText = allOpen ? "Collapse All" : "Expand All"
                      const handleToggleAll = () => {
                        if (allOpen) {
                          setOpenAccordionItems([])
                        } else {
                          setOpenAccordionItems(ALL_ACCORDION_ITEMS)
                        }
                      }
                      return (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleToggleAll}
                          className="bg-primary hover:bg-primary/90 text-white font-bold w-[7.5rem]"
                        >
                          {buttonText}
                        </Button>
                      )
                    })()}
                    {(session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER) && home?.id && (
                      <Button
                        type="button"
                        variant={reportedStatus ? "default" : "outline"}
                        size="sm"
                        className="shrink-0 w-[7.5rem] font-bold"
                        onClick={() => handleReportedStatusChange(home.id, !reportedStatus)}
                        disabled={reportedStatusLoading}
                        aria-label={reportedStatus ? "Set inactive" : "Set active"}
                      >
                        {reportedStatus ? "Active" : "Inactive"}
                      </Button>
                    )}
                  </div>
                </div>
                <OrganizationOptionsProvider>
                  <ProjectNotesTaggedProvider>
                    <UpdateHomeFormProvider key={`home-form-${home.id}`} home={home}>
                      <HomeDetailsFormAccordion openItems={openAccordionItems} setOpenItems={setOpenAccordionItems} />
                    </UpdateHomeFormProvider>
                  </ProjectNotesTaggedProvider>
                </OrganizationOptionsProvider>
              </>
            ) : (
              <div className="rounded-lg border p-5 text-center text-muted-foreground">
                {hasHome ? 'Loading home details…' : 'No home data available for this homeowner.'}
              </div>
            )}
          </>
        )}
        {activeTab === 'photos' && (
          <>
            {home ? (
              <PhotoTimelineTab homeId={home.id} homeownerId={homeOwner.id} homeownerName={homeOwner.user?.fullname} />
            ) : (

              <div className="rounded-lg border p-5 text-center text-muted-foreground">
                {hasHome ? 'Loading home details…' : 'No home data available for this homeowner.'}
              </div>
            )}
          </>
        )}
        {activeTab === 'workflow' && (
          <>
            {home ? (
              <WorkflowTab homeId={home.id} />
            ) : (
              <div className="rounded-lg border p-5 text-center text-muted-foreground">
                {hasHome ? 'Loading home details…' : 'No home data available for this homeowner.'}
              </div>
            )}
          </>
        )}
      </div>
      <ScrollToTopFab />
    </div>
  )
}

